import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "../contexts/AuthContext";
import {
  LayoutDashboard, Users, Building2, FolderTree, FileType2, Sparkles,
  Inbox, Search, Trash2, FileSearch, ScrollText, Mailbox, LogOut, Upload,
  ShieldAlert, ChevronLeft,
} from "lucide-react";

const NAV_BY_ROLE = {
  super_admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/explorer", label: "Document Explorer", icon: FolderTree },
    { to: "/upload", label: "Upload Center", icon: Upload },
    { to: "/search", label: "Search", icon: Search },
    { to: "/ocr-queue", label: "OCR Queue", icon: FileSearch },
    { divider: "Administration" },
    { to: "/users", label: "Users", icon: Users },
    { to: "/teams", label: "Teams", icon: Building2 },
    { to: "/folders", label: "Folders", icon: FolderTree },
    { to: "/document-types", label: "Document Types", icon: FileType2 },
    { to: "/ocr-rules", label: "OCR Rules", icon: Sparkles },
    { divider: "Operations" },
    { to: "/email-intake", label: "Email Intake Sim.", icon: Mailbox },
    { to: "/deletion-approvals", label: "Deletion Approvals", icon: ShieldAlert },
    { to: "/audit", label: "Audit Logs", icon: ScrollText },
  ],
  team_admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/explorer", label: "Document Explorer", icon: FolderTree },
    { to: "/upload", label: "Upload Center", icon: Upload },
    { to: "/search", label: "Search", icon: Search },
    { to: "/ocr-queue", label: "OCR Queue", icon: FileSearch },
    { divider: "Team" },
    { to: "/folders", label: "Folders", icon: FolderTree },
    { to: "/email-intake", label: "Email Intake Sim.", icon: Mailbox },
    { to: "/deletion-approvals", label: "Deletion Approvals", icon: ShieldAlert },
  ],
  scanner: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/explorer", label: "Document Explorer", icon: FolderTree },
    { to: "/upload", label: "Upload Center", icon: Upload },
    { to: "/ocr-queue", label: "OCR Queue", icon: FileSearch },
    { to: "/email-intake", label: "Email Intake Sim.", icon: Mailbox },
    { to: "/search", label: "Search", icon: Search },
  ],
  viewer: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/explorer", label: "Document Explorer", icon: FolderTree },
    { to: "/search", label: "Search", icon: Search },
  ],
};

export default function AppShell({ children, title, actions }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  if (!user) return null;
  const items = NAV_BY_ROLE[user.role] || [];

  return (
    <div className="flex min-h-screen" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? "w-14" : "w-60"} bg-white border-r border-[#E5E5E9] flex flex-col sticky top-0 h-screen transition-[width] duration-150`}
        data-testid="app-sidebar"
      >
        <div className="px-4 py-5 border-b border-[#E5E5E9] flex items-center justify-between">
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="w-7 h-7 bg-[#0A0A0A] flex items-center justify-center text-white text-xs font-bold font-mono">
              D
            </div>
            {!collapsed && (
              <div className="leading-tight">
                <div className="text-[13px] font-bold tracking-tight">DWELLSTONE</div>
                <div className="ds-overline" style={{ fontSize: 9 }}>
                  Document Vault
                </div>
              </div>
            )}
          </div>
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className="ds-btn-ghost p-1"
            aria-label="toggle sidebar"
          >
            <ChevronLeft size={14} className={collapsed ? "rotate-180" : ""} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {items.map((item, idx) => {
            if (item.divider) {
              return collapsed ? (
                <div key={idx} className="my-2 mx-3 border-t border-[#E5E5E9]" />
              ) : (
                <div key={idx} className="ds-overline px-4 mt-4 mb-2">
                  {item.divider}
                </div>
              );
            }
            const active = location.pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={`group flex items-center gap-3 px-4 py-2 text-[13px] border-l-2 transition-colors ${
                  active
                    ? "border-[#0A0A0A] bg-[#F7F7F8] text-[#0A0A0A] font-semibold"
                    : "border-transparent text-[#4A4A52] hover:bg-[#F7F7F8] hover:text-[#0A0A0A]"
                }`}
              >
                <Icon size={15} strokeWidth={2} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#E5E5E9] p-3">
          {!collapsed && (
            <div className="mb-2 leading-tight">
              <div className="text-[12px] font-semibold truncate">{user.name}</div>
              <div className="text-[11px] text-[#8A8A93] truncate">{user.email}</div>
              <div className="ds-overline mt-1">{ROLE_LABELS[user.role]}</div>
            </div>
          )}
          <button
            data-testid="logout-button"
            className={`ds-btn-secondary w-full ${collapsed ? "px-0" : ""}`}
            onClick={logout}
            title="Sign out"
          >
            <LogOut size={13} className="inline" />
            {!collapsed && <span className="ml-2">Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-[#E5E5E9] px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="ds-overline">Dwellstone DMS</div>
            <h1 className="text-xl font-bold tracking-tight font-display" data-testid="page-title">
              {title}
            </h1>
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
        <div className="flex-1 p-6 ds-anim-in">{children}</div>
      </main>
    </div>
  );
}
